import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import clsx from 'clsx'
import { ArrowLeft, Copy, Pencil, Search, X } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

import { apiService } from '../service/api'
import AppSidebar from '@/components/AppSidebar'
import DataSidebar from '@/components/DataSidebar'
import { Button } from '@/components/ui/button'
import { useNavigateBack } from '@/hooks/useNavigateBack'
import type { DocumentRecord } from '@/types/catalog'
import { ReviewTab } from '@/components/DocumentViewer/ReviewTab'
import { AiSummaryPane } from '@/components/UserArtifact/AiSummaryPane'
import { ExportMenu } from '@/components/UserArtifact/ExportMenu'
import { buildUserArtifactModel } from '@/components/UserArtifact/userArtifact'
import { copyTranscript } from '@/utils/transcriptExport'

type ArtifactTab = 'review' | 'summary'

/**
 * FS-557 (Phase 1) "User Artifacts" viewer — a call-transcript document.
 *
 * Two tabs: **Review** (the transcript markdown, editable + save-back via the
 * shared `ReviewTab`) and **AI Summary** (the generated summary). The toolbar
 * exposes search, a Download export menu (txt/md/doc), Copy, and Edit. The
 * body markdown is fetched via `downloadDocument`; structured bits come from
 * `documents.properties`.
 */
const UserArtifactViewer = () => {
  const { doc_id } = useParams()
  const { t } = useTranslation(['data_catalog', 'document_viewer', 'common'])
  const navigateBack = useNavigateBack()

  const [document, setDocument] = useState<DocumentRecord | null>(null)
  const [content, setContent] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [activeTab, setActiveTab] = useState<ArtifactTab>('review')
  const [isEditing, setIsEditing] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    const fetchArtifact = async () => {
      if (!doc_id) return
      setLoading(true)
      setError(null)
      try {
        const doc = await apiService.getDocument(doc_id)
        setDocument(doc)
        const blob = await apiService.downloadDocument(doc_id)
        setContent(await blob.text())
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : t('document_viewer:common.fetchError', { defaultValue: 'Error fetching document' }),
        )
      } finally {
        setLoading(false)
      }
    }
    void fetchArtifact()
  }, [doc_id, t])

  // Mirror ReviewTab's edited body into local state so Copy/Export stay in sync.
  const handleContentUpdate = (newContent: string) => {
    setContent(newContent)
  }

  // FS-557 PR6: persist the edited transcript body. This overwrites the
  // canonical markdown (documents.location) + the KG layout.md and re-ingests,
  // so the edit survives reload and the kg-agent tracks it — unlike the OCR
  // page-text store ReviewTab saves to by default.
  const handleSaveContent = async (newContent: string) => {
    if (!doc_id) return
    await apiService.updateTranscriptContent(doc_id, newContent)
  }

  const handleCopy = async () => {
    const model = document ? buildUserArtifactModel(document, content) : null
    const text = activeTab === 'summary' ? model?.summary ?? content : content
    const ok = await copyTranscript(text)
    if (ok) {
      toast.success(t('user_artifact.copied', { ns: 'document_viewer', defaultValue: 'Copied to clipboard' }))
    } else {
      toast.error(t('user_artifact.copyFailed', { ns: 'document_viewer', defaultValue: 'Failed to copy' }))
    }
  }

  const model = document ? buildUserArtifactModel(document, content) : null

  const tabs: { id: ArtifactTab; labelKey: string; labelDefault: string }[] = [
    { id: 'review', labelKey: 'tabs.review', labelDefault: 'Review' },
    { id: 'summary', labelKey: 'user_artifact.aiSummary', labelDefault: 'AI Summary' },
  ]

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-dark-text-muted">
          {t('document_viewer:common.loading', { defaultValue: 'Loading...' })}
        </div>
      )
    }
    if (error) {
      return <div className="flex-1 p-4 text-red-500">{error}</div>
    }
    if (activeTab === 'review') {
      return (
        <ReviewTab
          pageText={content}
          docId={doc_id}
          currentPage={1}
          onContentUpdate={handleContentUpdate}
          isEditing={isEditing}
          onEditingChange={setIsEditing}
          searchQuery={searchQuery}
          onSave={handleSaveContent}
        />
      )
    }
    return model ? <AiSummaryPane model={model} /> : null
  }

  return (
    <div className="flex h-screen relative">
      <AppSidebar />
      <DataSidebar />
      <div className="flex flex-1 flex-col min-w-0">
        {/* Top nav — matches the V2 Data Catalog shell */}
        <header className="flex h-[60px] shrink-0 items-center justify-between border-b border-[#e5e5e5] bg-white px-6 dark:border-dark-border dark:bg-dark-card">
          <button
            type="button"
            onClick={navigateBack}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 dark:text-dark-text-muted dark:hover:text-dark-text transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('nav:menu.data', 'Data')}
          </button>
          <button
            type="button"
            onClick={navigateBack}
            title={t('common:buttons.close')}
            className="p-2 text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-accent rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <main className="flex flex-1 flex-col min-h-0 bg-white dark:bg-dark-card">
          <div className="flex h-full w-full flex-col min-h-0">
        {/* Breadcrumb */}
        <div className="shrink-0 px-4 py-2 border-b border-gray-200 dark:border-dark-border flex items-center gap-1.5 text-sm">
          <span className="text-gray-500 dark:text-dark-text-muted">
            {t('nav:menu.data', 'Data')}
          </span>
          <span className="text-gray-400">/</span>
          <span className="font-medium text-gray-900 dark:text-dark-text truncate">
            {document?.name ?? t('document_viewer:common.loading', { defaultValue: 'Loading...' })}
          </span>
        </div>

        {/* Tabs */}
        <div className="shrink-0 flex gap-4 border-b border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card px-4">
          {tabs.map((tb) => (
            <button
              key={tb.id}
              onClick={() => setActiveTab(tb.id)}
              className={clsx(
                'pb-3 pt-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                activeTab === tb.id
                  ? 'text-blue-600 border-blue-600 dark:text-blue-400 dark:border-blue-400'
                  : 'text-gray-600 dark:text-gray-400 border-transparent hover:text-gray-900 dark:hover:text-dark-text',
              )}
            >
              {t(tb.labelKey, { ns: 'document_viewer', defaultValue: tb.labelDefault })}
            </button>
          ))}
        </div>

        {/* Toolbar: search + actions */}
        <div className="shrink-0 flex items-center justify-between gap-3 px-4 py-2 border-b border-gray-200 dark:border-dark-border">
          {/* Search only applies to the Review (transcript) tab; hide it on the
              AI Summary tab so a stale query can't sit unused. */}
          {activeTab === 'review' ? (
            <div className="relative max-w-xs flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('user_artifact.searchPlaceholder', { ns: 'document_viewer', defaultValue: 'Search transcript' })}
                className="w-full pl-8 pr-2 py-1.5 text-sm rounded-md border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          ) : (
            <div />
          )}
          <div className="flex items-center gap-2">
            <ExportMenu markdown={content} title={document?.name ?? 'transcript'} disabled={loading || !content} />
            <Button
              variant="outline"
              size="icon"
              onClick={handleCopy}
              disabled={loading || !content}
              title={t('user_artifact.copy', { ns: 'document_viewer', defaultValue: 'Copy' })}
              aria-label={t('user_artifact.copy', { ns: 'document_viewer', defaultValue: 'Copy' })}
            >
              <Copy className="w-4 h-4" />
            </Button>
            {activeTab === 'review' && !isEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(true)}
                disabled={loading || !content}
                className="gap-1.5"
              >
                <Pencil className="w-4 h-4" />
                {t('document_viewer:review.edit', { defaultValue: 'Edit' })}
              </Button>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-hidden">{renderBody()}</div>
          </div>
        </main>
      </div>
    </div>
  )
}

export default UserArtifactViewer
